import i18next from 'i18next';

import BaseApp from './App';

import { FileFormatClass } from '@egr/xbox/utils/FileFormats';
import { GeometryExportFormatKey } from '@egr/xbox/utils/FileFormats';
import { TrackingEventShareVia } from '@egr/xbox/telemetry/TrackingEvents';

import { ElementCategory, SceneElement } from '@egr/wcf/modules/core/mdl';
import { MainArticleElement } from '@egr/wcf/modules/cf';

export interface ShareImage {
    contentType: 'image/jpeg' | 'image/png' | 'image/gif';
    uri: string;
    measurements?: boolean;
    path?: string;
}

export type SharePDFTemplate = 'articlelist' | 'productsheet' | 'articlelist_mosaic' | 'gallery_mosaic';

export type GeometryShareFormat = '3DS' | 'DWG' | 'FBX' | 'GLB' | 'USDZ' | 'OBK';

export function isGeometryShareFormat(value: string): value is GeometryShareFormat {
    return [
        '3DS', 'DWG','FBX', 'GLB', 'USDZ', 'OBK'
    ].includes(value);
}

export type AdditionalShareFormat = 'snapshot' | 'pdf' | 'pbox' | 'gallery' | 'excel' | 'eox' | 'opl' | 'jpg' | 'planningUrl' | 'OBX' | 'USDZwithWalls';

export function isAdditionalShareFormat(value: string): value is AdditionalShareFormat {
    return [
        'snapshot', 'pdf', 'pbox', 'gallery', 'excel', 'eox', 'opl', 'jpg', 'planningUrl', 'OBX', 'USDZwithWalls'
    ].includes(value);
}

export function isExportableShareFormat(value: string): value is GeometryShareFormat | AdditionalShareFormat {
    return isGeometryShareFormat(value) || isAdditionalShareFormat(value);
}

export function exportRequiresBasketUpdate<T extends string>(formats: Array<T>): boolean {
    for (const format of formats) {
        if (['pdf', 'pbox', 'excel', 'OBK', 'OBX', 'DWG', '3DS', 'FBX', 'eox', 'opl'].includes(format)) {
            return true;
        }
    }

    return false;
}

export abstract class ShareCallbacks<App extends BaseApp = BaseApp> {
    protected abstract snapshotTrackingFileFormat: string;

    constructor(protected app: App) {
        this.shareError = this.shareError.bind(this);
        this.getShareActionFormat = this.getShareActionFormat.bind(this);
    }

    public abstract shareGeometry(
        fileFormat: GeometryExportFormatKey | null,
        target?: HTMLElement
    ): Promise<void>;

    public abstract shareUrl(uri: string, fileFormat: FileFormatClass, target?: HTMLElement): Promise<void>;

    protected getShareActionFormat(format: GeometryShareFormat | AdditionalShareFormat): TrackingEventShareVia['action'] {
        switch (format) {
            case 'pbox':
            case 'pdf':
            case 'excel':
            case 'eox':
            case 'opl':
                return format.toLocaleUpperCase();
            case 'snapshot':
                return  this.snapshotTrackingFileFormat;
            default:
                return format;
        }
    }

    public shareError(): void {
        this.app.messageManager.showMessage(i18next.t('Sharing failed'));
    }
}

export function isMainElement(element: SceneElement): element is MainArticleElement {
    return (
        element instanceof MainArticleElement &&
        element.category !== ElementCategory.Wall &&
        element.category !== ElementCategory.WallOpening
    );
}